import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { TextInputFormField } from 'packages/common-frontend/components/FormField';
import { useCreateLoanProductClientMutation } from 'packages/loanProduct/requests/requests.gql.generated';
import { GET_ORGANIZATION_LOAN_PRODUCTS } from 'packages/organization/requests/organization.gql';
import { FiPlusCircle } from 'react-icons/fi';
import { GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS } from '~/packages/implementations/requests/implementations.gql';

type Implementation = {
  id: string;
  name: string;
  slug: string;
  loanProducts: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  borrowerOrganization: {
    id: string;
    name: string;
    slug: string;
  };
  ownerOrganization: {
    id: string;
    name: string;
    slug: string;
  };
};

// Helpers
const getValidationSchema = (borrowerSlug: string) =>
  Yup.object().shape({
    name: Yup.string().trim().strict().required(),
    slug: Yup.string()
      .matches(
        new RegExp(`^${borrowerSlug}.*`),
        `loan product slug must start with the borrower slug '${borrowerSlug}'`,
      )
      .matches(
        /^[a-zA-Z0-9]*$/,
        'must be alphanumeric only, no spaces or symbols',
      )
      .required(),
  });

// Fragments
type AddLoanProductFormProps = {
  implementation: Implementation;
  onSuccess: () => void;
};
const AddLoanProductForm = ({
  implementation,
  onSuccess,
}: AddLoanProductFormProps) => {
  const [createLoanProduct] = useCreateLoanProductClientMutation({
    refetchQueries: [
      {
        query: GET_ORGANIZATION_LOAN_PRODUCTS,
        variables: { organizationId: implementation.borrowerOrganization.id },
      },
      {
        query: GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS,
        variables: { organizationId: implementation.ownerOrganization.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  return (
    <Box>
      <Formik
        initialValues={{
          name: 'Loan Product',
          slug: `${implementation.borrowerOrganization.slug}LoanProduct`,
        }}
        validationSchema={getValidationSchema(
          implementation.borrowerOrganization.slug,
        )}
        onSubmit={async (values) => {
          await createLoanProduct({
            variables: {
              ...values,
              implementationId: implementation.id,
            },
          });
          onSuccess();
        }}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit}>
            <VStack spacing="10px">
              <TextInputFormField label="Name" name="name" />
              <TextInputFormField label="Slug" name="slug" />
              <Button type="submit" isDisabled={formik.isSubmitting}>
                Add Loan Product
              </Button>
            </VStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

// Exports
type AddLoanProductButtonProps = {
  implementation: Implementation;
};
export const AddLoanProductButton = ({
  implementation,
}: AddLoanProductButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setIsOpen(true)} variant="outline">
        Add Loan Product <Icon marginLeft="50px" as={FiPlusCircle} />
      </Button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalOverlay />
        <ModalContent padding="20px">
          <ModalHeader>Add Loan Product</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb="20px">
              <AlertIcon />
              <AlertDescription>
                Deleting loan products is unsupported. Only the name field may
                be edited after creation.
              </AlertDescription>
            </Alert>
            <AddLoanProductForm
              implementation={implementation}
              onSuccess={() => setIsOpen(false)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
